import type { TFunction } from "i18next"
import {
  BanknoteIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  GiftIcon,
  LandmarkIcon,
  Loader2Icon,
  QrCodeIcon,
  ReceiptIcon,
  StarIcon,
  WalletIcon,
} from "lucide-react"
import { useState } from "react"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { PaymentMethodNameEnum } from "@/enums"
import { DateHelper } from "@/utils"

const PAYMENT_METHOD_ICONS: Record<
  PaymentMethodNameEnum,
  React.ElementType
> = {
  [PaymentMethodNameEnum.BALANCE]: WalletIcon,
  [PaymentMethodNameEnum.CREDIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.DEBIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.LOYALTY_POINTS]: StarIcon,
  [PaymentMethodNameEnum.GIFT_CARD_VTEX]: GiftIcon,
  [PaymentMethodNameEnum.OPEN_FINANCE]: LandmarkIcon,
  [PaymentMethodNameEnum.BANK_SLIP]: ReceiptIcon,
  [PaymentMethodNameEnum.BOLEPIX]: ReceiptIcon,
  [PaymentMethodNameEnum.DIRECT_DEBIT]: LandmarkIcon,
  [PaymentMethodNameEnum.NUPAY]: BanknoteIcon,
  [PaymentMethodNameEnum.PIX]: QrCodeIcon,
  [PaymentMethodNameEnum.PIX_AUTO]: QrCodeIcon,
  [PaymentMethodNameEnum.CREDIT_CARD_PRE_APPROVAL]:
    CreditCardIcon,
}

const getPaymentMethodName = (
  paymentMethod: PaymentMethodNameEnum | undefined,
  t: TFunction
): string => {
  if (!paymentMethod) return t("common.undefined")
  return t(`common.paymentMethods.${paymentMethod}`)
}

interface PixLinkPopoverProps {
  qrcode?: {
    image?: string
    text?: string
  }
  t: TFunction
}

const PixLinkPopover = ({
  qrcode,
  t,
}: PixLinkPopoverProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true)

  if (!qrcode?.text && !qrcode?.image) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
        >
          <QrCodeIcon size={12} />
          {t(
            "orders.transactionsTable.paymentLinks.pixCode"
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-2rem)] max-w-80 p-0"
        align="end"
        side="bottom"
        sideOffset={4}
      >
        <div className="border-b bg-muted/50 px-4 py-3">
          <h4 className="flex items-center gap-2 text-sm font-semibold">
            <QrCodeIcon size={14} />
            {t(
              "orders.transactionsTable.paymentLinks.pixInfo"
            )}
          </h4>
        </div>
        <div className="space-y-3 p-4">
          {qrcode.image && (
            <div className="flex flex-col items-center gap-2">
              <div className="relative h-32 w-32">
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-md border bg-muted/50">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2Icon
                        size={32}
                        className="animate-spin text-primary"
                      />
                      <span className="text-xs text-muted-foreground">
                        {t("common.loading")}
                      </span>
                    </div>
                  </div>
                )}
                <img
                  src={qrcode.image}
                  alt="QR Code Pix"
                  className="h-32 w-32 rounded-md border"
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                asChild
              >
                <a
                  href={qrcode.image}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLinkIcon size={12} />
                  {t(
                    "orders.transactionsTable.paymentLinks.openQrCode"
                  )}
                </a>
              </Button>
            </div>
          )}
          {qrcode.text && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t(
                  "orders.transactionsTable.paymentLinks.copyPaste"
                )}
              </span>
              <div className="flex items-center gap-1">
                <code className="max-h-20 flex-1 overflow-auto break-all rounded bg-muted px-2 py-1 text-xs">
                  {qrcode.text}
                </code>
                <ClipboardButton
                  text={qrcode.text}
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                />
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface BankSlipLinkPopoverProps {
  barcode?: {
    line?: string
    formatted?: string
    image?: string
  }
  urls?: {
    pdf?: string
    html?: string
  }
  dueDate?: string
  t: TFunction
}

const BankSlipLinkPopover = ({
  barcode,
  urls,
  dueDate,
  t,
}: BankSlipLinkPopoverProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true)

  if (!urls?.pdf && !barcode?.formatted) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
        >
          <ReceiptIcon size={12} />
          {t(
            "orders.transactionsTable.paymentLinks.bankSlipCode"
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-2rem)] max-w-96 p-0"
        align="end"
        side="bottom"
        sideOffset={4}
      >
        <div className="border-b bg-muted/50 px-4 py-3">
          <h4 className="flex items-center gap-2 text-sm font-semibold">
            <ReceiptIcon size={14} />
            {t(
              "orders.transactionsTable.paymentLinks.bankSlipInfo"
            )}
          </h4>
        </div>
        <div className="space-y-3 p-4">
          {urls?.pdf && (
            <Button
              variant="default"
              size="sm"
              className="w-full gap-1.5"
              asChild
            >
              <a
                href={urls.pdf}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLinkIcon size={14} />
                {t(
                  "orders.transactionsTable.paymentLinks.openBankSlip"
                )}
              </a>
            </Button>
          )}
          {barcode?.image && (
            <div className="flex flex-col items-center gap-2">
              <div className="relative h-16 w-full">
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-md border bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Loader2Icon
                        size={24}
                        className="animate-spin text-primary"
                      />
                      <span className="text-xs text-muted-foreground">
                        {t("common.loading")}
                      </span>
                    </div>
                  </div>
                )}
                <img
                  src={barcode.image}
                  alt="Barcode"
                  className="h-16 w-full rounded-md border object-contain"
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                />
              </div>
            </div>
          )}
          {barcode?.formatted && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t(
                  "orders.transactionsTable.paymentLinks.digitableLine"
                )}
              </span>
              <div className="flex items-center gap-1">
                <code className="flex-1 break-all rounded bg-muted px-2 py-1 text-xs">
                  {barcode.formatted}
                </code>
                <ClipboardButton
                  text={barcode.formatted}
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                />
              </div>
            </div>
          )}
          {barcode?.line && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t(
                  "orders.transactionsTable.paymentLinks.barCode"
                )}
              </span>
              <div className="flex items-center gap-1">
                <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">
                  {barcode.line}
                </code>
                <ClipboardButton
                  text={barcode.line}
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                />
              </div>
            </div>
          )}
          {dueDate && (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-muted-foreground">
                {t(
                  "orders.transactionsTable.paymentLinks.expiresAt"
                )}
              </span>
              <span className="text-sm">
                {DateHelper.format(dueDate, "dd/MM/yyyy")}
              </span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface PaymentMethodCellProps {
  transaction: TransactionsDetailsColumnsType
  t: TFunction
}

export const PaymentMethodCell = ({
  transaction,
  t,
}: PaymentMethodCellProps) => {
  const method = transaction.method as PaymentMethodNameEnum
  const Icon =
    PAYMENT_METHOD_ICONS[method] || CreditCardIcon

  const isCreditCard =
    method === PaymentMethodNameEnum.CREDIT_CARD
  const isDebitCard =
    method === PaymentMethodNameEnum.DEBIT_CARD
  const isCreditCardPreApproval =
    method ===
    PaymentMethodNameEnum.CREDIT_CARD_PRE_APPROVAL
  const isPix = method === PaymentMethodNameEnum.PIX
  const isPixAuto =
    method === PaymentMethodNameEnum.PIX_AUTO
  const isBankSlip =
    method === PaymentMethodNameEnum.BANK_SLIP
  const isBolepix = method === PaymentMethodNameEnum.BOLEPIX
  const isDirectDebit =
    method === PaymentMethodNameEnum.DIRECT_DEBIT

  const showCardInfo =
    isCreditCard || isDebitCard || isCreditCardPreApproval
  const showDirectDebitInfo = isDirectDebit
  const showPixLink = isPix || isBolepix || isPixAuto
  const showBankSlipLink = isBankSlip || isBolepix

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <Icon
            size={14}
            className="text-muted-foreground"
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            {getPaymentMethodName(method, t)}
          </span>
          {showCardInfo && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {transaction.logoUrl ? (
                  <img
                    src={transaction.logoUrl}
                    alt={transaction.brand || "card"}
                    className="h-5 w-auto object-contain"
                  />
                ) : (
                  transaction.brand && (
                    <Badge
                      variant="outline"
                      className="text-xs"
                    >
                      {transaction.brand}
                    </Badge>
                  )
                )}
                {transaction.lastFourDigits && (
                  <span className="text-xs text-muted-foreground">
                    •••• {transaction.lastFourDigits}
                  </span>
                )}
                {transaction.installmentCount &&
                  transaction.installmentCount > 1 && (
                    <Badge
                      variant="secondary"
                      className="text-xs"
                    >
                      {transaction.installmentCount}x
                    </Badge>
                  )}
              </div>
              {transaction.holder?.name && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-muted-foreground">
                    {transaction.holder.name}
                  </span>
                  {transaction.holder.taxId && (
                    <span className="text-xs text-muted-foreground">
                      {t("common.taxId")}:{" "}
                      {transaction.holder.taxId}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          {showDirectDebitInfo && (
            <div className="flex flex-col gap-1">
              {transaction.bank?.name && (
                <span className="text-xs text-muted-foreground">
                  {transaction.bank.name}
                </span>
              )}
              <div className="flex items-center gap-2">
                {transaction.agency && (
                  <Badge
                    variant="outline"
                    className="text-xs"
                  >
                    {t("orders.transactionsTable.agency")}:{" "}
                    {transaction.agency}
                  </Badge>
                )}
                {transaction.accountNumber && (
                  <span className="text-xs text-muted-foreground">
                    {t("orders.transactionsTable.account")}:{" "}
                    {transaction.accountNumber}
                  </span>
                )}
              </div>
              {transaction.accountType && (
                <Badge
                  variant="secondary"
                  className="w-fit text-xs"
                >
                  {t(
                    `orders.transactionsTable.accountTypes.${transaction.accountType}`
                  )}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
      {(showPixLink || showBankSlipLink) && (
        <div className="flex flex-wrap gap-1.5">
          {showPixLink && (
            <PixLinkPopover
              qrcode={transaction.qrcode}
              t={t}
            />
          )}
          {showBankSlipLink && (
            <BankSlipLinkPopover
              barcode={transaction.barcode}
              urls={transaction.urls}
              dueDate={transaction.dueDate}
              t={t}
            />
          )}
        </div>
      )}
    </div>
  )
}
